import { memo, useState, useCallback } from 'react';
import { Handle, Position, type NodeProps } from '@xyflow/react';
import { Shield, Database, Plus, Pencil, Trash2, GripVertical, ArrowDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useFlowBuilderStore } from '@/stores/flow-builder-store';
import { useUIStore } from '@/stores/ui-store';
import type { DefaultActionNodeData, MMMCard } from '@/types/field-mapping-flow';
import { generateDefaultFieldMappings } from '../default-field-mappings';

function makeRandomId(length = 7): string {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  let result = '';
  for (let i = 0; i < length; i++) {
    result += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return result;
}

function DefaultActionNodeComponent({ data }: NodeProps) {
  const nodeData = data as unknown as DefaultActionNodeData;
  const { docId, cards, hasConfiguredCards, targetModule, targetModuleLabel, isMultiModule, ignoreNewCustomers, ignoreExistingCustomers } = nodeData;

  const selectBranch = useFlowBuilderStore((s) => s.selectBranch);
  const selectCard = useFlowBuilderStore((s) => s.selectCard);
  const removeCard = useFlowBuilderStore((s) => s.removeCard);
  const addCard = useFlowBuilderStore((s) => s.addCard);
  const setActiveSidebarSection = useFlowBuilderStore((s) => s.setActiveSidebarSection);
  const setFieldMappingPanelOpen = useFlowBuilderStore((s) => s.setFieldMappingPanelOpen);
  const selectedBranchId = useFlowBuilderStore((s) => s.selectedBranchId);
  const isDark = useUIStore((s) => s.theme) === 'dark';

  const isSelected = selectedBranchId === docId;
  const [isDragOver, setIsDragOver] = useState(false);

  // Internal card reorder state
  const [dragCardIndex, setDragCardIndex] = useState<number | null>(null);
  const [dropTargetIndex, setDropTargetIndex] = useState<number | null>(null);
  const reorderCards = useFlowBuilderStore((s) => s.reorderCards);

  const handleCardClick = (cardId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (docId) {
      selectBranch(docId);
      selectCard(cardId);
    }
  };

  const handleRemoveCard = (cardId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (docId) {
      removeCard(docId, cardId);
    }
  };

  const handleAddCard = () => {
    if (docId) {
      selectBranch(docId);
      setActiveSidebarSection('modules');
    }
  };

  // ── Module drop from sidebar ──
  const handleNodeDragOver = useCallback((e: React.DragEvent) => {
    if (e.dataTransfer.types.includes('application/flow-module')) {
      if (!isMultiModule && cards.length > 0) return;
      e.preventDefault();
      e.stopPropagation();
      e.dataTransfer.dropEffect = 'move';
      setIsDragOver(true);
    }
  }, [isMultiModule, cards.length]);

  const handleNodeDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  }, []);

  const handleNodeDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);

    const moduleDataStr = e.dataTransfer.getData('application/flow-module');
    if (!moduleDataStr || !docId) return;
    if (!isMultiModule && cards.length > 0) return;

    try {
      const { moduleValue, moduleLabel } = JSON.parse(moduleDataStr) as {
        moduleValue: string;
        moduleLabel: string;
      };

      const store = useFlowBuilderStore.getState();
      const mapping = store.fieldMappings[docId];
      if (mapping?.fields && mapping.isMMM) {
        const existingCards = Object.values(mapping.fields).filter(
          (val): val is MMMCard =>
            typeof val === 'object' && val !== null && 'cardId' in val
        );
        if (existingCards.some((c) => c.saasFieldModuleName === moduleValue)) return;
      }

      const cardId = makeRandomId(7);
      const defaults = generateDefaultFieldMappings(moduleValue);
      const newCard: MMMCard = {
        cardId,
        saasFieldModuleName: moduleValue,
        displayLabel: moduleLabel,
        orderOf: cards.length,
        fields: defaults.fields,
        fields_type: defaults.fields_type,
        ct: Date.now(),
      };

      selectBranch(docId);
      addCard(docId, newCard);
    } catch {
      // ignore
    }
  }, [docId, cards.length, selectBranch, addCard, isMultiModule]);

  // ── Internal card reorder ──
  const handleCardDragStart = (e: React.DragEvent, index: number) => {
    e.stopPropagation();
    e.dataTransfer.setData('application/card-reorder', String(index));
    e.dataTransfer.effectAllowed = 'move';
    setDragCardIndex(index);
  };

  const handleCardDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.dataTransfer.types.includes('application/card-reorder')) {
      e.dataTransfer.dropEffect = 'move';
      setDropTargetIndex(index);
    }
  };

  const handleCardDrop = (e: React.DragEvent, toIndex: number) => {
    e.preventDefault();
    e.stopPropagation();
    const fromIndexStr = e.dataTransfer.getData('application/card-reorder');
    if (fromIndexStr === '' || !docId) return;
    const fromIndex = parseInt(fromIndexStr, 10);
    if (fromIndex !== toIndex) {
      reorderCards(docId, fromIndex, toIndex);
    }
    setDragCardIndex(null);
    setDropTargetIndex(null);
  };

  const handleCardDragEnd = () => {
    setDragCardIndex(null);
    setDropTargetIndex(null);
  };

  // ── Dark-mode-aware colors (indigo/violet theme) ──
  const nodeBg = isDark
    ? 'linear-gradient(135deg, #1e1b4b 0%, #312e81 100%)'
    : 'linear-gradient(135deg, #eef2ff 0%, #e0e7ff 100%)';
  const nodeBorder = isDark
    ? `1px solid ${isDragOver ? '#818cf8' : isSelected ? '#a78bfa' : '#3730a3'}`
    : `1px solid ${isDragOver ? '#818cf8' : isSelected ? '#a78bfa' : '#c7d2fe'}`;
  const dragOverBg = isDark
    ? 'linear-gradient(135deg, #312e81 0%, #3730a3 100%)'
    : 'linear-gradient(135deg, #e8eaff 0%, #d4d8ff 100%)';
  const headerBorderColor = isDark ? '#3730a3' : '#c7d2fe';
  const iconBg = isDark ? '#4f46e5' : '#6366f1';
  const labelColor = isDark ? '#a5b4fc' : '#6366f1';
  const addBtnColor = isDark ? '#a5b4fc' : '#6366f1';
  const handleDotBg = isDark ? '#1e1b4b' : 'white';
  const emptyBorderColor = isDark ? '#3730a3' : '#c7d2fe';
  const emptyBg = isDark ? 'rgba(20, 20, 40, 0.5)' : 'rgba(255,255,255,0.5)';
  const cardItemBg = isDark ? 'rgba(15, 15, 42, 0.6)' : 'rgba(255,255,255,0.7)';
  const cardItemBorder = isDark ? '#3730a3' : '#e0e7ff';
  const cardItemDropBorder = isDark ? '#a78bfa' : '#818cf8';
  const arrowColor = isDark ? '#3730a3' : '#c7d2fe';

  return (
    <div
      className={`
        rounded-xl shadow-md transition-all min-w-[260px] max-w-[300px]
        ${isSelected ? 'ring-2 ring-indigo-300/40' : 'hover:shadow-lg'}
        ${isDragOver ? 'ring-2 ring-indigo-400/60' : ''}
      `}
      style={{
        background: isDragOver ? dragOverBg : nodeBg,
        border: nodeBorder,
      }}
      onDragOver={handleNodeDragOver}
      onDragLeave={handleNodeDragLeave}
      onDrop={handleNodeDrop}
    >
      <Handle
        type="target"
        position={Position.Top}
        className="!h-2.5 !w-2.5 !border-2"
        style={{ borderColor: '#6366f1', background: handleDotBg }}
      />

      {/* Header */}
      <div
        className="flex items-center gap-2.5 px-3.5 py-2.5"
        style={{ borderBottom: `1px solid ${headerBorderColor}` }}
      >
        <div
          className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg shadow-sm"
          style={{ background: iconBg }}
        >
          <Shield className="h-3.5 w-3.5 text-white" />
        </div>
        <div className="min-w-0 flex-1">
          <p
            className="text-[10px] font-bold uppercase tracking-wider"
            style={{ color: labelColor }}
          >
            Default Actions
          </p>
          <p className="text-[10px] text-muted-foreground">
            Runs when no condition matches
          </p>
        </div>
      </div>

      {/* Cards or placeholder */}
      <div className="p-2 space-y-0">
        {hasConfiguredCards && cards.length > 0 ? (
          cards.map((card: MMMCard, index: number) => {
            const createFieldCount = Object.keys(card.fields || {}).length;
            const updateFieldCount = Object.keys(card.fieldsForExisting?.fields || {}).length;
            const hasExisting = updateFieldCount > 0;
            const isDragging = dragCardIndex === index;
            const isDropTarget = dropTargetIndex === index && dragCardIndex !== null && dragCardIndex !== index;

            return (
              <div key={card.cardId}>
                {index > 0 && (
                  <div className="flex justify-center py-1">
                    <ArrowDown className="h-3 w-3" style={{ color: arrowColor }} />
                  </div>
                )}
                <div
                  draggable={isMultiModule}
                  onDragStart={isMultiModule ? (e) => handleCardDragStart(e, index) : undefined}
                  onDragOver={isMultiModule ? (e) => handleCardDragOver(e, index) : undefined}
                  onDrop={isMultiModule ? (e) => handleCardDrop(e, index) : undefined}
                  onDragEnd={isMultiModule ? handleCardDragEnd : undefined}
                  className={`
                    nodrag group flex cursor-pointer items-center gap-3 rounded-lg px-3 py-2.5 transition-all
                    ${isDragging ? 'opacity-40' : ''}
                    ${isDropTarget ? 'ring-2 ring-indigo-400 ring-offset-1' : ''}
                  `}
                  style={{
                    background: cardItemBg,
                    border: `1px solid ${isDropTarget ? cardItemDropBorder : cardItemBorder}`,
                  }}
                  onClick={(e) => handleCardClick(card.cardId, e)}
                >
                  {isMultiModule && (
                    <GripVertical className="h-3 w-3 shrink-0 cursor-grab active:cursor-grabbing text-muted-foreground/50 group-hover:text-muted-foreground" />
                  )}
                  <div
                    className="relative flex h-6 w-6 shrink-0 items-center justify-center rounded-md"
                    style={{ background: iconBg }}
                  >
                    <Database className="h-3 w-3 text-white" />
                    {isMultiModule && (
                      <span
                        className="absolute -top-1.5 -right-1.5 flex h-3.5 w-3.5 items-center justify-center rounded-full text-[8px] font-bold text-white"
                        style={{ background: '#4338ca' }}
                      >
                        {index + 1}
                      </span>
                    )}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-xs font-medium text-gray-900 dark:text-foreground">
                      {card.displayLabel || card.saasFieldModuleName}
                    </p>
                    <div className="flex items-center gap-1.5">
                      <span className="text-[10px] text-muted-foreground">
                        {hasExisting
                          ? `${createFieldCount} create \u00b7 ${updateFieldCount} update`
                          : `${createFieldCount} ${createFieldCount === 1 ? 'field' : 'fields'}`}
                      </span>
                    </div>
                    {ignoreNewCustomers && (
                      <p className="text-[9px] font-medium text-orange-600 dark:text-orange-400 whitespace-nowrap">Proceed only with existing records</p>
                    )}
                    {ignoreExistingCustomers && (
                      <p className="text-[9px] font-medium text-orange-600 dark:text-orange-400 whitespace-nowrap">Proceed only with new records</p>
                    )}
                  </div>
                  <div className="flex shrink-0 items-center gap-0.5 opacity-0 transition-opacity group-hover:opacity-100">
                    <button
                      onClick={(e) => handleCardClick(card.cardId, e)}
                      className="flex h-5 w-5 items-center justify-center rounded text-muted-foreground hover:text-foreground"
                      title="Edit"
                    >
                      <Pencil className="h-2.5 w-2.5" />
                    </button>
                    <button
                      onClick={(e) => handleRemoveCard(card.cardId, e)}
                      className="flex h-5 w-5 items-center justify-center rounded text-muted-foreground hover:bg-red-500/10 hover:text-red-500"
                      title="Remove"
                    >
                      <Trash2 className="h-2.5 w-2.5" />
                    </button>
                  </div>
                </div>
              </div>
            );
          })
        ) : (
          <button
            className="nodrag flex w-full items-center gap-2.5 rounded-lg px-3 py-3 text-left transition-colors hover:opacity-80"
            style={{ border: `1px dashed ${emptyBorderColor}`, background: emptyBg }}
            onClick={(e) => {
              e.stopPropagation();
              if (docId) {
                selectBranch(docId);
                // Without a resolved default module (e.g. Monday/Airtable, or any
                // app whose default is missing) open the module picker so the user
                // can choose one — don't auto-create a card with an empty module.
                if (!targetModule) {
                  setActiveSidebarSection('modules');
                  return;
                }
                // Auto-create a card with default field mappings from TPA + SaaS schemas
                const cardId = makeRandomId(7);
                const defaults = generateDefaultFieldMappings(targetModule);
                const newCard: MMMCard = {
                  cardId,
                  saasFieldModuleName: targetModule,
                  displayLabel: targetModuleLabel || targetModule,
                  orderOf: 0,
                  fields: defaults.fields,
                  fields_type: defaults.fields_type,
                  ct: Date.now(),
                };
                addCard(docId, newCard);
                selectCard(cardId);
              }
            }}
          >
            <div
              className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md"
              style={{ background: iconBg }}
            >
              <Database className="h-3 w-3 text-white" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-[11px] font-medium" style={{ color: addBtnColor }}>
                {targetModuleLabel || targetModule || 'Select a module to continue'}
              </p>
              <p className="text-[10px] text-muted-foreground/70">
                {targetModule ? 'Click to configure field mapping' : 'Choose a module to start field mapping'}
              </p>
              {ignoreNewCustomers && (
                <p className="text-[9px] font-medium text-orange-600 dark:text-orange-400 whitespace-nowrap">Proceed only with existing records</p>
              )}
              {ignoreExistingCustomers && (
                <p className="text-[9px] font-medium text-orange-600 dark:text-orange-400 whitespace-nowrap">Proceed only with new records</p>
              )}
            </div>
          </button>
        )}

        {/* Add card button — multi-module only */}
        {isMultiModule && (
          <Button
            variant="ghost"
            size="sm"
            className="mt-1.5 w-full justify-start gap-1.5 text-xs"
            style={{ color: addBtnColor }}
            onClick={handleAddCard}
          >
            <Plus className="h-3 w-3" />
            Add Module Card
          </Button>
        )}
      </div>

      <Handle
        type="source"
        position={Position.Bottom}
        className="!h-2.5 !w-2.5 !border-2"
        style={{ borderColor: '#6366f1', background: handleDotBg }}
      />
    </div>
  );
}

export const DefaultActionNode = memo(DefaultActionNodeComponent);
